/**
 * Service Issues Queue: installer-facing triage list of system-detected
 * and manually-created issues awaiting action.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  XCircle,
  CalendarDays,
  ArrowRight,
  RefreshCw,
  ClipboardList,
  ImageIcon,
  KeyRound,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  useServiceIssues,
  useServiceIssueCounts,
  useTransitionIssue,
  type ServiceIssue,
  type TransitionParams,
} from '@/hooks/useServiceIssues';
import { getTriggerLabel } from '@/lib/automation-trigger-types';

// ─── Severity badge ─────────────────────────────────────────────────────────

function severityBadge(severity: string) {
  const classes: Record<string, string> = {
    critical: 'bg-wt-red-primary/15 text-wt-red-primary border-wt-red-primary/30',
    high: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
    medium: 'bg-wt-amber-primary/15 text-wt-amber-primary border-wt-amber-primary/30',
    low: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
    info: 'bg-wt-green-primary/15 text-wt-green-primary border-wt-green-primary/30',
  };
  return (
    <Badge variant="outline" className={cn('text-[11px] capitalize', classes[severity] ?? '')}>
      {severity}
    </Badge>
  );
}

// ─── Status badge ───────────────────────────────────────────────────────────

function statusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    detected: { label: 'New', className: 'bg-wt-red-primary/10 text-wt-red-primary' },
    reviewed: { label: 'Reviewed', className: 'bg-wt-amber-primary/10 text-wt-amber-primary' },
    scheduled: { label: 'Scheduled', className: 'bg-blue-500/10 text-blue-500' },
    in_progress: { label: 'In Progress', className: 'bg-purple-500/10 text-purple-500' },
    resolved: { label: 'Resolved', className: 'bg-wt-green-primary/10 text-wt-green-primary' },
    dismissed: { label: 'Dismissed', className: 'bg-zinc-500/10 text-zinc-400' },
  };
  const m = map[status] ?? { label: status, className: '' };
  return <Badge variant="outline" className={cn('text-[11px]', m.className)}>{m.label}</Badge>;
}

// ─── Resolve/Dismiss dialog ─────────────────────────────────────────────────

type ResolutionDialogProps = {
  open: boolean;
  mode: 'resolve' | 'dismiss';
  onClose: () => void;
  onConfirm: (resolutionType: string, notes: string) => void;
};

function ResolutionDialog({ open, mode, onClose, onConfirm }: ResolutionDialogProps) {
  const [resolutionType, setResolutionType] = useState(mode === 'resolve' ? 'fixed' : 'not_an_issue');
  const [notes, setNotes] = useState('');

  const options = mode === 'resolve'
    ? [
        { value: 'fixed', label: 'Fixed' },
        { value: 'customer_resolved', label: 'Customer resolved' },
        { value: 'deferred', label: 'Deferred' },
      ]
    : [
        { value: 'not_an_issue', label: 'Not an issue' },
        { value: 'deferred', label: 'Deferred / will monitor' },
      ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'resolve' ? 'Resolve Issue' : 'Dismiss Issue'}</DialogTitle>
          <DialogDescription>
            {mode === 'resolve' ? 'Mark this issue as resolved.' : 'Dismiss this issue from the queue.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Resolution type</Label>
            <Select value={resolutionType} onValueChange={setResolutionType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was done..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant={mode === 'resolve' ? 'default' : 'secondary'}
            onClick={() => { onConfirm(resolutionType, notes); onClose(); }}
          >
            {mode === 'resolve' ? 'Resolve' : 'Dismiss'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Schedule dialog (now uses ScheduleVisitDialog to create a real visit) ──
import { ScheduleVisitDialog } from '@/components/visits/ScheduleVisitDialog';

// ─── Customer-submitted photos ──────────────────────────────────────────────

function CustomerPhotos({ photoPaths }: { photoPaths: string[] }) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const urls: Record<string, string> = {};
      for (const path of photoPaths) {
        const { data } = await supabase.storage
          .from('service-photos')
          .createSignedUrl(path, 3600);
        if (data?.signedUrl) urls[path] = data.signedUrl;
      }
      if (!cancelled) {
        setSignedUrls(urls);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [photoPaths]);

  if (loading) {
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground">Customer photos</h4>
        <div className="flex gap-2">
          {photoPaths.map((_, i) => (
            <Skeleton key={i} className="w-20 h-20 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  if (Object.keys(signedUrls).length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">Customer photos</h4>
      <div className="flex gap-2 flex-wrap">
        {photoPaths.map((path) => {
          const url = signedUrls[path];
          if (!url) return null;
          return (
            <a
              key={path}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-20 h-20 rounded-md overflow-hidden border border-border hover:ring-2 hover:ring-wt-accent transition-shadow"
            >
              <img
                src={url}
                alt="Customer photo"
                className="w-full h-full object-cover"
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ─── Issue detail sheet ─────────────────────────────────────────────────────

function IssueDetail({ issue, onTransition }: { issue: ServiceIssue; onTransition: (p: TransitionParams) => void }) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const isOpen = !['resolved', 'dismissed'].includes(issue.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {severityBadge(issue.severity)}
          {statusBadge(issue.status)}
          <span className="text-xs text-muted-foreground capitalize">{issue.source}</span>
        </div>
        <h3 className="text-lg font-medium text-foreground">{issue.title}</h3>
        <Link
          to={`/app/property/${issue.property_id}`}
          className="text-sm text-wt-accent hover:underline"
        >
          {issue.property_name}
        </Link>
      </div>

      {/* Reconnect CTA for provider credential-failure issues */}
      {isOpen && ['provider_credential_invalid', 'provider_credential_mismatch'].includes(issue.issue_type) && (
        <Button asChild size="sm">
          <Link
            to={
              ((issue.trigger_snapshot as Record<string, unknown>)?.cta_deeplink as string) ||
              '/app/settings?tab=integrations'
            }
          >
            <KeyRound className="h-4 w-4 mr-1.5" />
            Reconnect {((issue.trigger_snapshot as Record<string, unknown>)?.provider_label as string) || 'account'}
          </Link>
        </Button>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-muted-foreground">Detected</span>
          <p className="font-medium">{format(new Date(issue.created_at), 'MMM d, yyyy h:mm a')}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Issue type</span>
          <p className="font-medium">{getTriggerLabel(issue.issue_type)}</p>
        </div>
        {issue.source === 'customer' && issue.homeowner_reported_category && (
          <div>
            <span className="text-muted-foreground">Customer category</span>
            <p className="font-medium capitalize">{issue.homeowner_reported_category.replace(/_/g, ' ')}</p>
          </div>
        )}
        {issue.scheduled_for && (
          <div>
            <span className="text-muted-foreground">Scheduled for</span>
            <p className="font-medium">{format(new Date(issue.scheduled_for), 'MMM d, yyyy')}</p>
          </div>
        )}
        {issue.resolved_at && (
          <div>
            <span className="text-muted-foreground">Resolved</span>
            <p className="font-medium">{format(new Date(issue.resolved_at), 'MMM d, yyyy h:mm a')}</p>
          </div>
        )}
        {issue.resolution_type && (
          <div>
            <span className="text-muted-foreground">Resolution</span>
            <p className="font-medium capitalize">{issue.resolution_type.replace(/_/g, ' ')}</p>
          </div>
        )}
      </div>

      {/* Customer-submitted photos */}
      {issue.source === 'customer' && issue.photo_paths?.length > 0 && (
        <CustomerPhotos photoPaths={issue.photo_paths} />
      )}

      {/* Evidence summary (structured, from automation) */}
      {issue.source === 'automation' && (issue.trigger_snapshot as Record<string, unknown>)?._evidence && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Detection evidence</h4>
          <div className="rounded-md bg-wt-bg-secondary p-3 text-xs space-y-1.5">
            {(() => {
              const ev = (issue.trigger_snapshot as Record<string, unknown>)._evidence as Record<string, unknown>;
              const EVIDENCE_LABELS: Record<string, string> = {
                automation_rule_name: 'Rule',
                event_kind: 'Event',
                detected_at: 'Detected',
                soc_pct: 'SoC %',
                available_kwh: 'Available kWh',
                time_to_reserve_hours: 'Time to reserve (h)',
                breach_probability: 'Breach probability',
                confidence: 'Confidence',
                wasted_today_kwh: 'Wasted today (kWh)',
                pv_kw: 'PV (kW)',
                load_kw: 'Load (kW)',
                online: 'Online',
                data_stale: 'Data stale',
                provider: 'Provider',
              };
              return Object.entries(ev)
                .filter(([k]) => k !== 'automation_rule_id')
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="text-muted-foreground">{EVIDENCE_LABELS[k] ?? k.replace(/_/g, ' ')}</span>
                    <span className="font-mono text-foreground truncate max-w-[200px]">
                      {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v ?? '—')}
                    </span>
                  </div>
                ));
            })()}
          </div>
        </div>
      )}

      {/* Trigger snapshot (raw system state) */}
      {issue.trigger_snapshot && Object.keys(issue.trigger_snapshot).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">System state at detection</h4>
          <div className="rounded-md bg-wt-bg-secondary p-3 text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
            {Object.entries(issue.trigger_snapshot)
              .filter(([k]) => !['property_name', '_evidence'].includes(k))
              .map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-foreground truncate max-w-[200px]">{String(v ?? '—')}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {issue.resolution_notes && (
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-muted-foreground">Resolution notes</h4>
          <p className="text-sm">{issue.resolution_notes}</p>
        </div>
      )}

      {/* Actions */}
      {isOpen && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          {issue.status === 'detected' && (
            <Button size="sm" variant="outline" onClick={() => onTransition({ issueId: issue.id, newStatus: 'reviewed' })}>
              <Eye className="h-3.5 w-3.5 mr-1.5" /> Mark Reviewed
            </Button>
          )}
          {['detected', 'reviewed'].includes(issue.status) && (
            <Button size="sm" variant="outline" onClick={() => setScheduleOpen(true)}>
              <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Schedule
            </Button>
          )}
          <Button size="sm" variant="default" onClick={() => setResolveOpen(true)}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Resolve
          </Button>
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setDismissOpen(true)}>
            <XCircle className="h-3.5 w-3.5 mr-1.5" /> Dismiss
          </Button>
        </div>
      )}

      <ResolutionDialog
        open={resolveOpen}
        mode="resolve"
        onClose={() => setResolveOpen(false)}
        onConfirm={(rt, notes) => onTransition({ issueId: issue.id, newStatus: 'resolved', resolutionType: rt, resolutionNotes: notes })}
      />
      <ResolutionDialog
        open={dismissOpen}
        mode="dismiss"
        onClose={() => setDismissOpen(false)}
        onConfirm={(rt, notes) => onTransition({ issueId: issue.id, newStatus: 'dismissed', resolutionType: rt, resolutionNotes: notes })}
      />
      <ScheduleVisitDialog
        open={scheduleOpen}
        onOpenChange={(o) => !o && setScheduleOpen(false)}
        preselectedPropertyId={issue.property_id}
        issueId={issue.id}
        issueTitle={issue.title}
        onIssueScheduled={() => setScheduleOpen(false)}
      />
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function Issues() {
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [selectedIssue, setSelectedIssue] = useState<ServiceIssue | null>(null);

  // "open" = not resolved/dismissed
  const effectiveStatusFilter = statusFilter === 'open' ? undefined : statusFilter;
  const { issues, isLoading, refetch } = useServiceIssues({
    status: effectiveStatusFilter,
    severity: severityFilter === 'all' ? undefined : severityFilter,
  });
  const { counts } = useServiceIssueCounts();
  const transition = useTransitionIssue();

  // Filter open issues client-side when "open" selected (covers detected + reviewed + scheduled + in_progress)
  const displayIssues = statusFilter === 'open'
    ? issues.filter((i) => !['resolved', 'dismissed'].includes(i.status))
    : issues;

  const openCount = counts.detected + counts.reviewed + counts.scheduled + counts.in_progress;

  const handleTransition = (params: TransitionParams) => {
    transition.mutate(params, {
      onSuccess: () => {
        toast.success(`Issue ${params.newStatus === 'resolved' ? 'resolved' : params.newStatus === 'dismissed' ? 'dismissed' : 'updated'}`);
        if (['resolved', 'dismissed'].includes(params.newStatus)) {
          setSelectedIssue(null);
        }
      },
      onError: (err) => {
        toast.error(`Failed: ${(err as Error).message}`);
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Service Issues</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {openCount} open issue{openCount !== 1 ? 's' : ''} requiring attention
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open ({openCount})</SelectItem>
            <SelectItem value="detected">New ({counts.detected})</SelectItem>
            <SelectItem value="reviewed">Reviewed ({counts.reviewed})</SelectItem>
            <SelectItem value="scheduled">Scheduled ({counts.scheduled})</SelectItem>
            <SelectItem value="in_progress">In Progress ({counts.in_progress})</SelectItem>
            <SelectItem value="resolved">Resolved ({counts.resolved})</SelectItem>
            <SelectItem value="dismissed">Dismissed ({counts.dismissed})</SelectItem>
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : displayIssues.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ClipboardList className="h-12 w-12 text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No issues found</p>
          <p className="text-xs text-muted-foreground mt-1">
            {statusFilter === 'open' ? 'All clear — no open issues.' : 'Try a different filter.'}
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-wt-bg-secondary/50">
                <TableHead className="w-[80px]">Severity</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[120px]">Detected</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayIssues.map((issue) => (
                <TableRow
                  key={issue.id}
                  className="cursor-pointer hover:bg-wt-bg-secondary/30"
                  onClick={() => setSelectedIssue(issue)}
                >
                  <TableCell>{severityBadge(issue.severity)}</TableCell>
                  <TableCell className="font-medium text-sm">{issue.property_name}</TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate max-w-[300px]">{issue.title}</p>
                        {issue.source === 'customer' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-500 border-blue-500/30 shrink-0">
                            Customer
                          </Badge>
                        )}
                        {issue.photo_paths?.length > 0 && (
                          <span className="flex items-center gap-0.5 text-muted-foreground shrink-0" title={`${issue.photo_paths.length} photo(s)`}>
                            <ImageIcon className="w-3 h-3" />
                            <span className="text-[10px]">{issue.photo_paths.length}</span>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{getTriggerLabel(issue.issue_type)}</p>
                    </div>
                  </TableCell>
                  <TableCell>{statusBadge(issue.status)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail sheet */}
      <Sheet open={!!selectedIssue} onOpenChange={(o) => !o && setSelectedIssue(null)}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Issue Detail</SheetTitle>
          </SheetHeader>
          {selectedIssue && (
            <div className="mt-4">
              <IssueDetail issue={selectedIssue} onTransition={handleTransition} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
